import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { ModalCard } from 'partial/components/Modal';

import AsyncButton from 'modules/common/components/AsyncButton';
import FormInput from 'partial/components/form/FormInput';

import { useAddDataset, useEditDataset } from '../hooks';

const validationSchema = yup.object().shape({
  label: yup
    .string()
    .min(5, 'Too short')
    .max(50, 'Too long')
    .required('Required'),
  value: yup.string().required('Required'),
});

function AddUpdate({ onClose, data, type, code, editValueOnly }) {
  const [pending, setPending] = useState(false);
  const { values, setFieldValue, errors } = useFormik({
    initialValues: {
      label: data?.label || '',
      value: data?.value || '',
    },
    validationSchema,
    validateOnChange: false,
  });

  const isFieldEmpty = useMemo(
    () => !values?.label.length || !values?.value.length,
    [values],
  );
  const onError = () => setPending(false);
  const onSuccess = () => {
    setPending(false);
    onClose();
  };

  const [isAddLoading, add] = useAddDataset(code, { onSuccess, onError });
  const [isEditLoading, edit] = useEditDataset(code, { onSuccess, onError });

  const handleSubmit = (e) => {
    e.preventDefault();
    setPending(true);

    if (type === 'add') add(values);
    if (type === 'edit') edit({ ...values, id: data?.id });
  };

  return (
    <ModalCard
      size="sm"
      title={`${type === 'add' ? 'ADD' : 'EDIT'} DATASET`}
      onClose={onClose}
    >
      <form className="px-4 py-6" onSubmit={handleSubmit}>
        <div className="space-y-6">
          <FormInput
            name="label"
            onSetFieldValue={setFieldValue}
            value={values?.label}
            error={errors.label}
            label="Label"
            placeholder="Enter Label"
            type="text"
            required
            disabled={editValueOnly}
          />
          <FormInput
            name="value"
            onSetFieldValue={setFieldValue}
            value={values?.value}
            error={errors.value}
            label="Value"
            placeholder="Enter Value"
            type="text"
            required
          />

          <div className="flex justify-end gap-2 mt-10">
            <AsyncButton
              color="outline"
              className="font-[400] !text-neutral-500"
              onClick={onClose}
            >
              Cancel
            </AsyncButton>
            <AsyncButton
              type="submit"
              className="font-[400]"
              color="outline"
              disabled={isFieldEmpty}
              isLoading={pending || isAddLoading || isEditLoading}
            >
              {type === 'add' ? 'save' : 'save changes'}
            </AsyncButton>
          </div>
        </div>
      </form>
    </ModalCard>
  );
}

AddUpdate.defaultProps = {
  type: 'add',
  data: {},
  editValueOnly: false,
};

AddUpdate.propTypes = {
  onClose: PropTypes.func.isRequired,
  type: PropTypes.string,
  data: PropTypes.instanceOf(Object),
  code: PropTypes.string.isRequired,
  editValueOnly: PropTypes.bool,
};

export default AddUpdate;
